import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { api, sanitizeEmbed } from "@/lib/api";

export default function ThankYou() {
  const [cs, setCs] = useState(null);
  useEffect(() => { api.get("/content/case-study").then(({ data }) => setCs(data)).catch(() => {}); }, []);
  const title = cs?.title || "A NATIONAL BRAND FILM, DELIVERED IN 3 WEEKS";
  const description = cs?.description || "When a fast-growing beverage brand needed a hero campaign film on a tight timeline, Splash assembled a full crew — director, cinematographer, gaffer and editor — within 48 hours. The result was a cinematic 90-second spot that drove a 3x lift in engagement across paid social, delivered on budget and ahead of schedule.";

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-noise">
      <header className="border-b border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex items-center justify-between">
          <img src={LOGO_URL} alt="Splash Creative Hub" className="w-[150px] h-auto object-contain" />
          <Link data-testid="thankyou-home-link" to="/" className="text-sm text-[#A1A1AA] hover:text-white inline-flex items-center gap-2 transition-colors"><ArrowLeft className="w-4 h-4" /> Back to home</Link>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-8 lg:px-12 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl" data-testid="thank-you">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF4D00] mb-4">Inquiry Received</div>
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mb-6">THANKS FOR<br /><span className="text-[#FF4D00]">REACHING OUT</span></h1>
          <p className="text-base text-[#A1A1AA] leading-relaxed">
            We've received your project details and a member of the Splash team will be in touch shortly.
            Keep an eye on your inbox — we've also sent a confirmation to your email.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-16 grid lg:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="case-study">
          {/* Video placeholder */}
          <div className="bg-[#0A0A0A] relative min-h-[300px] overflow-hidden flex items-center" data-testid="case-study-video">
            {cs?.embed_code ? (
              <div className="w-full embed-fill" dangerouslySetInnerHTML={{ __html: sanitizeEmbed(cs.embed_code) }} />
            ) : (
              <div className="absolute inset-0 group cursor-pointer">
                <img src="/hero-split.jpg" alt="Featured case study" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#FF4D00] flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg" data-testid="case-study-play">
                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.25em] text-white/80">Showreel · Video coming soon</div>
              </div>
            )}
          </div>
          {/* Case study copy */}
          <div className="bg-[#0A0A0A] p-8 lg:p-12">
            <div className="text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-4">Featured Case Study</div>
            <h2 className="font-bebas text-4xl leading-[0.95] mb-4" data-testid="case-study-title">{title}</h2>
            <p className="text-sm text-[#A1A1AA] leading-relaxed whitespace-pre-line" data-testid="case-study-description">{description}</p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
